import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { formatErnMap, parseErnMap, type ManifestBlock } from '../../tarifs/model/manifest-form';
import { ManifestBlockField } from '../../tarifs/ui/PublishWizard';
import { provisionalCandidates } from '../model/provisional-refs';

/**
 * One of the three ref maps — documents, workflows, settings — with a dropdown in front of the
 * textarea rather than instead of it.
 *
 * # Why the dropdown is marked provisional, out loud
 *
 * documentservice and workflowservice do not exist, and the asset registry port answers only "is
 * this ref vouched for?" — it cannot enumerate candidates. So the options here are the ERNs this
 * tarif's own published versions already pin (`provisionalCandidates`), which invents nothing and
 * is also the most likely answer: a manifest is authored by taking the last one and moving a pin.
 *
 * What it is NOT is a catalog. A ref no previous version used will never appear, and a brand-new
 * tarif's lists are empty — which is the true statement, not a bug. Saying so in the UI is what
 * stops an operator concluding that an absent option means an absent document.
 *
 * # Why the textarea stays
 *
 * As the escape hatch, and only as that. It is the way to name a ref no dropdown knows, which for
 * a first version is every ref. Making it the primary control again would undo the point of this
 * screen; removing it would make first versions unauthorable.
 */
export function ProvisionalRefMapField({
  block,
  value,
  onChange,
  error,
  manifests,
}: {
  readonly block: ManifestBlock;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly error: string;
  /** The manifests of this owner's published versions — the only source of candidates. */
  readonly manifests: readonly unknown[];
}): ReactNode {
  const { t } = useTranslation('insurance');
  const candidates = provisionalCandidates(block, manifests);
  const { entries } = parseErnMap(value);

  const add = (key: string, ern: string): void => {
    onChange(formatErnMap({ ...entries, [key]: ern }));
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle2" component="span">
          {t(`tarifs.manifest.blocks.${block}` as never)}
        </Typography>
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={t('authoring.version.provisional')}
        />
      </Stack>

      {candidates.length === 0 ? (
        // The honest empty state. "There is no dropdown yet" is a very different statement from
        // "there are no documents", and only the first one is true.
        <Alert severity="info">{t('authoring.version.noCandidates')}</Alert>
      ) : (
        <>
          <Alert severity="info">{t('authoring.version.provisionalHint')}</Alert>
          <TextField
            select
            size="small"
            label={t('authoring.version.addRef')}
            value=""
            fullWidth
            helperText={t('authoring.version.addRefHint')}
            onChange={(event) => {
              const chosen = candidates.find(
                (candidate) => `${candidate.key}=${candidate.ern}` === event.target.value,
              );
              if (chosen !== undefined) add(chosen.key, chosen.ern);
            }}
          >
            {candidates.map((candidate) => (
              <MenuItem
                key={`${candidate.key}=${candidate.ern}`}
                value={`${candidate.key}=${candidate.ern}`}
              >
                <Stack>
                  <Box component="span">{candidate.key}</Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {candidate.ern}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </TextField>
        </>
      )}

      {/* The escape hatch, and what the dropdown writes into. */}
      <ManifestBlockField block={block} value={value} onChange={onChange} error={error} />
    </Stack>
  );
}
